import { useState, useEffect, useRef } from 'react'
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Label, Select } from '../components/ui'
import { useCompany } from '../hooks/useCompany'
import { useAuth } from '../hooks/useAuth'
import { Plus, Trash2, Clock, AlertCircle } from 'lucide-react'

interface SchedulingPreference {
  id: string
  type: 'max_hours_per_week' | 'max_consecutive_days' | 'preferred_shift' | 'avoid_shift' | 'min_hours_between_shifts'
  value: string
  priority: number
}

export default function Availability() {
  const { currentCompany } = useCompany()
  const { } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [currentTeamId, setCurrentTeamId] = useState<string | null>(null)
  const [availability, setAvailability] = useState<Set<string>>(new Set())
  const [preferences, setPreferences] = useState<SchedulingPreference[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState<{ day: number; hour: number } | null>(null)
  const [dragEnd, setDragEnd] = useState<{ day: number; hour: number } | null>(null)
  const [showPreferenceModal, setShowPreferenceModal] = useState(false)
  const [newPreference, setNewPreference] = useState<Partial<SchedulingPreference>>({
    type: 'max_hours_per_week',
    value: ''
  })
  const gridRef = useRef<HTMLDivElement>(null)

  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  const hours = Array.from({ length: 24 }, (_, i) => i) // 0-23

  // Fetch teams for current company
  useEffect(() => {
    const fetchTeamsAndSetCurrent = async () => {
      if (!currentCompany?.id) {
        setCurrentTeamId(null)
        setLoading(false)
        return
      }

      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}/v1/teams?company_id=${currentCompany.id}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            }
          }
        )

        if (!response.ok) {
          throw new Error('Failed to fetch teams')
        }

        const data = await response.json()
        const teams = data.teams || []
        
        // Find a team where the user is a member
        const userTeam = teams.find((team: any) => team.user_role !== null)
        
        if (userTeam) {
          setCurrentTeamId(userTeam.id)
        } else if (teams.length > 0) {
          setCurrentTeamId(teams[0].id)
        } else {
          setCurrentTeamId(null)
          setError('No teams found in this company')
        }
      } catch (err) {
        console.error('Failed to fetch teams:', err)
        setCurrentTeamId(null)
        setError('Failed to load teams')
      }
    }

    fetchTeamsAndSetCurrent()
  }, [currentCompany?.id])

  // Fetch availability data
  useEffect(() => {
    if (!currentTeamId) {
      return
    }

    fetchAvailability()
  }, [currentTeamId])

  const fetchAvailability = async () => {
    if (!currentTeamId) return

    try {
      setLoading(true)
      setError(null)

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/availability?team_id=${currentTeamId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      )

      if (!response.ok) {
        throw new Error('Failed to fetch availability')
      }

      const data = await response.json()
      
      // Convert availability data to Set format for the UI
      const availabilitySet = new Set<string>()
      for (const slot of data.availability || []) {
        // Create entries for each hour in the range
        for (let hour = slot.start_hour; hour < slot.end_hour; hour++) {
          availabilitySet.add(`${slot.day_of_week}-${hour}`)
        }
      }

      setAvailability(availabilitySet)
      setPreferences(data.preferences || [])
    } catch (err) {
      console.error('Failed to fetch availability:', err)
      setError(err instanceof Error ? err.message : 'Failed to load availability')
    } finally {
      setLoading(false)
    }
  }

  const getCellKey = (day: number, hour: number): string => {
    return `${day}-${hour}`
  }

  const handleMouseDown = (day: number, hour: number) => {
    setIsDragging(true)
    setDragStart({ day, hour })
    setDragEnd({ day, hour })
  }

  const handleMouseMove = (day: number, hour: number) => {
    if (isDragging && dragStart) {
      setDragEnd({ day, hour })
    }
  }

  const handleMouseUp = () => {
    if (isDragging && dragStart && dragEnd) {
      // Calculate the rectangle bounds
      const minDay = Math.min(dragStart.day, dragEnd.day)
      const maxDay = Math.max(dragStart.day, dragEnd.day)
      const minHour = Math.min(dragStart.hour, dragEnd.hour)
      const maxHour = Math.max(dragStart.hour, dragEnd.hour)

      // Toggle all cells within the rectangle
      setAvailability(prev => {
        const newSet = new Set(prev)
        for (let day = minDay; day <= maxDay; day++) {
          for (let hour = minHour; hour <= maxHour; hour++) {
            const key = getCellKey(day, hour)
            if (newSet.has(key)) {
              newSet.delete(key)
            } else {
              newSet.add(key)
            }
          }
        }
        return newSet
      })
    }

    setIsDragging(false)
    setDragStart(null)
    setDragEnd(null)
  }

  const isTimeSlotActive = (day: number, hour: number): boolean => {
    return availability.has(getCellKey(day, hour))
  }

  const isInDragSelection = (day: number, hour: number): boolean => {
    if (!isDragging || !dragStart || !dragEnd) return false
    
    const minDay = Math.min(dragStart.day, dragEnd.day)
    const maxDay = Math.max(dragStart.day, dragEnd.day)
    const minHour = Math.min(dragStart.hour, dragEnd.hour)
    const maxHour = Math.max(dragStart.hour, dragEnd.hour)
    
    return day >= minDay && day <= maxDay && hour >= minHour && hour <= maxHour
  }

  const addPreference = () => {
    if (!newPreference.type || !newPreference.value) return

    const preference: SchedulingPreference = {
      id: Date.now().toString(),
      type: newPreference.type!,
      value: newPreference.value,
      priority: preferences.length + 1
    }

    setPreferences([...preferences, preference])
    setNewPreference({ type: 'max_hours_per_week', value: '' })
    setShowPreferenceModal(false)
  }

  const removePreference = (id: string) => {
    const filtered = preferences.filter(p => p.id !== id)
    // Re-number priorities
    const updated = filtered.map((p, index) => ({ ...p, priority: index + 1 }))
    setPreferences(updated)
  }

  const movePreference = (id: string, direction: 'up' | 'down') => {
    const index = preferences.findIndex(p => p.id === id)
    if (index === -1) return

    const newPreferences = [...preferences]
    if (direction === 'up' && index > 0) {
      [newPreferences[index], newPreferences[index - 1]] = [newPreferences[index - 1], newPreferences[index]]
    } else if (direction === 'down' && index < preferences.length - 1) {
      [newPreferences[index], newPreferences[index + 1]] = [newPreferences[index + 1], newPreferences[index]]
    }

    // Re-number priorities
    const updated = newPreferences.map((p, index) => ({ ...p, priority: index + 1 }))
    setPreferences(updated)
  }

  const getPreferenceLabel = (pref: SchedulingPreference): string => {
    switch (pref.type) {
      case 'max_hours_per_week':
        return `Maximum ${pref.value} hours per week`
      case 'max_consecutive_days':
        return `Maximum ${pref.value} consecutive days`
      case 'preferred_shift':
        return `Prefer ${pref.value} shifts`
      case 'avoid_shift':
        return `Avoid ${pref.value} shifts`
      case 'min_hours_between_shifts':
        return `Minimum ${pref.value} hours between shifts`
      default:
        return pref.value
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)

      // Convert Set to array format for API
      const availabilityArray = Array.from(availability)

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/availability`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            teamId: currentTeamId,
            availability: availabilityArray,
            preferences: preferences
          })
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to save availability')
      }

      // Show success message
      alert('Availability saved successfully!')
      
      // Refresh the data
      await fetchAvailability()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save availability')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-48 mb-6"></div>
          <div className="space-y-4">
            <div className="h-96 bg-gray-200 rounded"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error && !currentTeamId) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">My Availability</h1>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {/* Weekly Availability Grid */}
      <Card>
        <CardHeader>
          <CardTitle>Weekly Availability</CardTitle>
          <CardDescription>
            Click and drag on the calendar below to set your available hours. You can select multiple days at once.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div 
              ref={gridRef}
              className="select-none"
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {/* Header */}
              <div className="grid grid-cols-8 border-b border-gray-200">
                <div className="p-2 bg-gray-50 text-sm font-medium text-gray-700"></div>
                {days.map((day) => (
                  <div key={day} className="p-2 bg-gray-50 text-sm font-medium text-gray-700 text-center">
                    {day}
                  </div>
                ))}
              </div>

              {/* Time Grid */}
              {hours.map((hour) => (
                <div key={hour} className="grid grid-cols-8 border-b border-gray-100">
                  <div className="p-2 bg-gray-50 text-xs text-gray-600 text-right pr-3">
                    {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
                  </div>
                  {days.map((_, dayIndex) => (
                    <div
                      key={`${dayIndex}-${hour}`}
                      className={`
                        border-r border-gray-100 cursor-pointer transition-colors
                        ${isTimeSlotActive(dayIndex, hour) 
                          ? isInDragSelection(dayIndex, hour) 
                            ? 'bg-blue-300' 
                            : 'bg-blue-500 hover:bg-blue-600' 
                          : isInDragSelection(dayIndex, hour) 
                            ? 'bg-gray-200' 
                            : 'bg-white hover:bg-gray-50'}
                      `}
                      onMouseDown={() => handleMouseDown(dayIndex, hour)}
                      onMouseEnter={() => handleMouseMove(dayIndex, hour)}
                      style={{ height: '40px' }}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Legend */}
          <div className="mt-4 flex items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-500 rounded"></div>
              <span>Available</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-white border border-gray-300 rounded"></div>
              <span>Not Available</span>
            </div>
            <div className="ml-auto text-gray-500">
              Click and drag to toggle availability
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scheduling Preferences */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Scheduling Preferences</CardTitle>
              <CardDescription>
                Set your preferences for the scheduling algorithm. Higher priority rules will be considered first.
              </CardDescription>
            </div>
            <Button size="sm" onClick={() => setShowPreferenceModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Preference
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {preferences.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No scheduling preferences set yet.</p>
              <p className="text-sm mt-1">Add preferences to customize how you're scheduled.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {preferences.map((pref, index) => (
                <div key={pref.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg group">
                  <div className="flex items-center flex-1">
                    <div className="flex flex-col mr-3">
                      <button
                        onClick={() => movePreference(pref.id, 'up')}
                        disabled={index === 0}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => movePreference(pref.id, 'down')}
                        disabled={index === preferences.length - 1}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                    <div className="flex items-center">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-medium mr-3">
                        {pref.priority}
                      </span>
                      <span className="text-sm">{getPreferenceLabel(pref)}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => removePreference(pref.id)}
                    className="text-red-600 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Preference Modal */}
      {showPreferenceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Add Scheduling Preference</h2>
              <button
                onClick={() => {
                  setShowPreferenceModal(false)
                  setNewPreference({ type: 'max_hours_per_week', value: '' })
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <Label htmlFor="preference-type">Preference Type</Label>
                <Select
                  id="preference-type"
                  value={newPreference.type}
                  onChange={(e) => setNewPreference({ ...newPreference, type: e.target.value as SchedulingPreference['type'] })}
                  className="mt-1"
                >
                  <option value="max_hours_per_week">Maximum hours per week</option>
                  <option value="max_consecutive_days">Maximum consecutive days</option>
                  <option value="preferred_shift">Preferred shift time</option>
                  <option value="avoid_shift">Avoid shift time</option>
                  <option value="min_hours_between_shifts">Minimum hours between shifts</option>
                </Select>
              </div>

              <div>
                <Label htmlFor="preference-value">Value</Label>
                {newPreference.type === 'preferred_shift' || newPreference.type === 'avoid_shift' ? (
                  <Select
                    id="preference-value"
                    value={newPreference.value}
                    onChange={(e) => setNewPreference({ ...newPreference, value: e.target.value })}
                    className="mt-1"
                  >
                    <option value="">Select...</option>
                    <option value="morning">Morning (6 AM - 12 PM)</option>
                    <option value="afternoon">Afternoon (12 PM - 6 PM)</option>
                    <option value="evening">Evening (6 PM - 12 AM)</option>
                    <option value="overnight">Overnight (12 AM - 6 AM)</option>
                  </Select>
                ) : (
                  <input
                    id="preference-value"
                    type="number"
                    value={newPreference.value}
                    onChange={(e) => setNewPreference({ ...newPreference, value: e.target.value })}
                    placeholder={
                      newPreference.type === 'max_hours_per_week' ? 'e.g., 40' :
                      newPreference.type === 'max_consecutive_days' ? 'e.g., 5' :
                      newPreference.type === 'min_hours_between_shifts' ? 'e.g., 8' : ''
                    }
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="1"
                  />
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowPreferenceModal(false)
                  setNewPreference({ type: 'max_hours_per_week', value: '' })
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={addPreference}
                disabled={!newPreference.value}
              >
                Add Preference
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}